/**
 *  NYOMTATÁS KÜLÖN KERETBE (iframe) – a nyomtatványkép papírra.
 *
 *  A képernyőt nem nyomtatjuk: az Ionic-keret menüje, fejléce is papírra kerülne, a görgetett
 *  tartalom pedig levágódna. A lapok egy rejtett tárolóban renderelődnek, majd a kitöltött
 *  értékekkel együtt egy különálló keretbe másolva nyomtatjuk (mint a NAPLO nyomtatása).
 *
 *  A kitöltő és a kötegelt nyomtatás ugyanezt használja.
 */

/** A4 álló, 8 mm margóval – 96 dpi-s képpontban, a lapkép kicsinyítéséhez. */
import { ablakNyomtat } from '../nyomtatas-elonezet';

export const A4 = { szeles: Math.round(((210 - 16) / 25.4) * 96), magas: Math.round(((297 - 16) / 25.4) * 96) };

/**
 *  Egy sablonlap kicsinyítése A4-re.
 *  A lapkép a sablon koordinátáiban (px) áll, ezért arányosan skálázzuk. Nem `zoom`-mal: a
 *  böngészők a zoomolt lapokat nem mindig törik el helyesen, és akkor az egész köteg egyetlen
 *  oldalra csúszik – transzformációnál a befoglaló doboz mérete pontosan tartja a lapszakítást.
 */
export function lapArany(oldal: { w: number; h: number }): number {
  const sz = (oldal?.w || 760) + 20, mg = (oldal?.h || 1100) + 20;
  return Math.min(A4.szeles / sz, A4.magas / mg, 1);
}

/** Kép (pl. a fejléc címere) adat-URI-ként: a rejtett keretben a böngésző a letöltést elhalaszthatja. */
export async function kepAdatUri(url: string | null): Promise<string | null> {
  if (!url) return null;
  if (/^data:/.test(url)) return url;
  try {
    const b = await (await fetch(url)).blob();
    return await new Promise<string>((ok, hiba) => {
      const fr = new FileReader();
      fr.onload = () => ok(String(fr.result));
      fr.onerror = () => hiba(fr.error);
      fr.readAsDataURL(b);
    });
  } catch { return url; }
}

/**
 *  A megadott tároló tartalmának nyomtatása külön keretben.
 *  @param forras   a rejtett tároló (a lapokkal)
 *  @param cim      a nyomtatási dokumentum címe (a böngésző fejléce/fájlneve)
 *  @param logo     { url, adatUri } – a fejléc címere beágyazva
 */
/** Az űrlapelemek aktuális értéke attribútumba – a másolat csak az attribútumokat viszi át. */
function ertekekAttributumba(forras: HTMLElement) {
  forras.querySelectorAll('input').forEach((i: HTMLInputElement) => {
    if (i.type === 'checkbox') { if (i.checked) i.setAttribute('checked', ''); else i.removeAttribute('checked'); }
    else i.setAttribute('value', i.value);
  });
  forras.querySelectorAll('select').forEach((sel: HTMLSelectElement) => {
    Array.from(sel.options).forEach((o, k) => { if (k === sel.selectedIndex) o.setAttribute('selected', ''); else o.removeAttribute('selected'); });
  });
}

/** A nyomtatási lap saját szabályai (a keretben és a PDF-ben ugyanazok). */
const NYOMTATAS_STILUS = `
      html, body { margin: 0; background: #fff; }
      .any-nyomtat { display: block !important; }
      .any-nyomtat-lap { break-after: page; page-break-after: always; break-inside: avoid; overflow: hidden; }
      .any-nyomtat-lap:last-child { break-after: auto; page-break-after: auto; }
      .any-nyomtat-tart { transform-origin: top left; }
      .anyk-oldal { box-shadow: none !important; margin: 0 !important; }
      .anyk-lap-keret { margin: 0 !important; }   /* a lap kerete tartja a középre igazítást – papíron balra zárva */
      .anyk-mezo input[type="text"], .anyk-mezo select { background: #fff !important; }
      @page { size: A4 portrait; margin: 8mm; }`;

/**
 *  ÖNÁLLÓ HTML a PDF-hez (a kiszolgáló alakítja PDF-fé – nav-nyomtatvany.js `/bevallas/:id/pdf`).
 *  A kiszolgáló renderelője semmit nem tölt le: a stílusok szövegként, a képek adat-URI-ként mennek.
 */
export async function onalloHtml(forras: HTMLElement, cim: string): Promise<string> {
  ertekekAttributumba(forras);
  const masolat = forras.cloneNode(true) as HTMLElement;
  for (const img of Array.from(masolat.querySelectorAll('img'))) {
    const src = img.getAttribute('src') || '';
    //  A relatív cím az alkalmazás gyökeréhez (base href) képest értendő, nem az aktuális útvonalhoz.
    if (src) img.setAttribute('src', (await kepAdatUri(new URL(src, document.baseURI).href)) || src);
  }
  //  A stíluslapok szabályai szövegként (a saját eredetű lapoké olvasható; az idegen kimarad).
  const css: string[] = [];
  for (const lap of Array.from(document.styleSheets)) {
    try { css.push(Array.from(lap.cssRules).map((r) => r.cssText).join('\n')); } catch { /* idegen eredetű lap */ }
  }
  const esc = (t: string) => t.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]!));
  return `<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>${esc(cim)}</title>
    <style>${css.join('\n').replace(/<\/style/gi, '<\\/style')}</style><style>${NYOMTATAS_STILUS}
      /* Az Ionic a <body>-t rögzíti (position: fixed, overflow: hidden) – PDF-ben az első lap után minden levágódna. */
      html, body { position: static !important; height: auto !important; overflow: visible !important; }</style></head>
    <body>${masolat.outerHTML}</body></html>`;
}

export function keretbeNyomtat(forras: HTMLElement, cim: string, logo?: { url?: string | null; adatUri?: string | null }) {
  ertekekAttributumba(forras);
  // A képek abszolút címmel – a keretben a relatív cím nem biztosan oldódik fel.
  forras.querySelectorAll('img').forEach((img: HTMLImageElement) => {
    const src = img.getAttribute('src') || '';
    img.setAttribute('src', logo?.adatUri && src === logo.url ? logo.adatUri : new URL(src, location.href).href);
  });
  const stilusok = Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]')).map((e) => e.outerHTML).join('\n');
  const keret = document.createElement('iframe');
  keret.setAttribute('aria-hidden', 'true');
  keret.style.cssText = 'position:fixed;left:-10000px;top:0;width:820px;height:1200px;border:0;visibility:hidden';
  document.body.appendChild(keret);
  const doc = keret.contentDocument!;
  doc.open();
  doc.write(`<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>${cim}</title>${stilusok}
    <style>${NYOMTATAS_STILUS}
    </style></head><body>${forras.outerHTML}</body></html>`);
  doc.close();
  const takarit = () => setTimeout(() => keret.remove(), 1000);
  keret.contentWindow!.addEventListener('afterprint', takarit);
  // Nyomtatás csak a képek betöltése után (különben a fejléc ikonja lemarad a papírról).
  const kepek = Array.from(doc.images).map((i) => (i.complete ? Promise.resolve() : new Promise((ok) => { i.onload = i.onerror = () => ok(null); })));
  return Promise.race([Promise.all(kepek), new Promise((ok) => setTimeout(ok, 3000))]).then(() => new Promise<void>((kesz) => setTimeout(() => {
    try { ablakNyomtat(keret.contentWindow!, cim); } finally { setTimeout(takarit, 60000); kesz(); }
  }, 200)));
}
